import { useState, useRef, useCallback, type DragEvent } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useAnalyzeImage } from '../../hooks/useInventory';
import { useTranslation } from '../../i18n';
import { useAppMode } from '../../context/AppModeContext';
import { AiUsageMeter } from '../ai/AiUsageMeter';
import styles from './ImageAnalyzer.module.css';

const MAX_PX = 1024;
const JPEG_QUALITY = 0.85;

/** Resize an image to fit within MAX_PX and compress as JPEG. */
function resizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let { width, height } = img;
      if (width <= MAX_PX && height <= MAX_PX && file.size <= 3 * 1024 * 1024) {
        return resolve(file);
      }
      if (width > MAX_PX || height > MAX_PX) {
        const scale = MAX_PX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Compression failed'));
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

interface AnalysisData {
  name: string;
  category: string;
  platform: string;
  subcategory: string | null;
  condition: string;
  videoSystem: string | null;
  description: string;
  confidence: 'high' | 'medium' | 'low';
  cpu?: string | null;
  memory?: string | null;
  launchDate?: string | null;
  launchPrice?: string | null;
  launchPriceCurrency?: string | null;
  launchPriceInflationAdjusted?: string | null;
}

interface ImageAnalyzerProps {
  onAnalysisComplete: (data: AnalysisData, imageFile: File) => void;
  onImageSelected?: (file: File | null) => void;
  /** Controlled image state — when provided, the component uses these instead of internal state */
  imageFile?: File | null;
  imagePreviewUrl?: string | null;
  onImageChange?: (file: File | null, previewUrl: string | null) => void;
}

const AiSvgIcon = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
    <rect x="2" y="8" width="32" height="32" rx="6" stroke="currentColor" strokeWidth="2.5" fill="none" />
    <path d="M12 32l5-16h2l5 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M13.5 27h9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M28 16v16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M36 4c0 3.5-2 6-2 6s-2-2.5-2-6c0-3.5 2-4 2-4s2 .5 2 4z" fill="currentColor" />
    <path d="M30 8c3.5 0 6 2 6 2s-2.5 2-6 2c-3.5 0-4-2-4-2s.5-2 4-2z" fill="currentColor" />
    <path d="M40.5 12c0 1.5-1 2.5-1 2.5s-1-1-1-2.5 1-2 1-2 1 .5 1 2z" fill="currentColor" opacity="0.5" />
    <path d="M37.5 14c1.5 0 2.5 1 2.5 1s-1 1-2.5 1-2-1-2-1 .5-1 2-1z" fill="currentColor" opacity="0.5" />
  </svg>
);

export function ImageAnalyzer({ onAnalysisComplete, onImageSelected, imageFile: controlledFile, imagePreviewUrl: controlledPreviewUrl, onImageChange }: ImageAnalyzerProps) {
  const { t } = useTranslation();
  const { hasAiAccess, aiTier, aiUsage } = useAppMode();
  const [internalFile, setInternalFile] = useState<File | null>(null);
  const [internalPreviewUrl, setInternalPreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analyzeMutation = useAnalyzeImage();

  // Use controlled state if provided, otherwise internal state
  const isControlled = controlledFile !== undefined;
  const selectedFile = isControlled ? controlledFile : internalFile;
  const previewUrl = isControlled ? controlledPreviewUrl ?? null : internalPreviewUrl;

  const setImage = useCallback((file: File | null, url: string | null) => {
    if (isControlled) {
      onImageChange?.(file, url);
    } else {
      setInternalFile(file);
      setInternalPreviewUrl(url);
    }
  }, [isControlled, onImageChange]);

  const handleFile = useCallback(async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('aiScan.invalidType'));
      return;
    }
    setError(null);
    try {
      const resized = await resizeImage(file);
      const url = URL.createObjectURL(resized);
      setImage(resized, url);
      onImageSelected?.(resized);
    } catch {
      setError(t('aiScan.failed'));
    }
  }, [t, setImage, onImageSelected]);

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function handleClear() {
    if (previewUrl && !isControlled) URL.revokeObjectURL(previewUrl);
    setImage(null, null);
    setError(null);
    analyzeMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    onImageSelected?.(null);
  }

  async function handleAnalyze() {
    if (!selectedFile) return;
    setError(null);
    try {
      const result = await analyzeMutation.mutateAsync(selectedFile);
      onAnalysisComplete(result, selectedFile);
    } catch {
      setError(t('aiScan.failed'));
    }
  }

  // ── Preview state: thumbnail + actions + status in one row ──
  if (selectedFile) {
    return (
      <div className={styles.analyzer}>
        <div className={styles.preview}>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              className={styles.previewImage}
              onClick={() => setShowFullPreview(true)}
              title={t('aiScan.clickToEnlarge')}
            />
          )}

          {analyzeMutation.isPending ? (
            <div className={styles.loadingOverlay}>
              <div className={styles.spinner} />
              {t('aiScan.analyzing')}
            </div>
          ) : (
            <div className={styles.previewActions}>
              {hasAiAccess && (
                <Button type="button" size="small" onClick={handleAnalyze} disabled={analyzeMutation.isPending}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.35rem', verticalAlign: 'middle', display: 'inline' }}>
                    <path d="M9.5 2l.5 4 4 .5-4 .5-.5 4-.5-4-4-.5 4-.5z" fill="currentColor" />
                    <path d="M19 8l.5 3 3 .5-3 .5-.5 3-.5-3-3-.5 3-.5z" fill="currentColor" />
                    <path d="M11 15l.5 3 3 .5-3 .5-.5 3-.5-3-3-.5 3-.5z" fill="currentColor" />
                  </svg>
                  {t('aiScan.analyze')}
                </Button>
              )}
              <Button type="button" size="small" variant="secondary" onClick={handleClear}>
                {t('aiScan.clear')}
              </Button>
            </div>
          )}

          <div className={styles.previewStatus}>
            {error && <div className={styles.statusMessage}><div className={styles.error}>{error}</div></div>}
            {analyzeMutation.isSuccess && analyzeMutation.data?.confidence === 'low' && (
              <div className={styles.statusMessage}><div className={styles.warning}>{t('aiScan.lowConfidence')}</div></div>
            )}
            {analyzeMutation.isSuccess && analyzeMutation.data?.confidence !== 'low' && (
              <div className={styles.statusMessage}><div className={styles.success}>{t('aiScan.prefilled')}</div></div>
            )}
            {(aiTier === 'pro' || aiUsage) && <AiUsageMeter />}
          </div>
        </div>

        {previewUrl && (
          <Modal
            isOpen={showFullPreview}
            onClose={() => setShowFullPreview(false)}
            title={t('aiScan.imagePreview')}
            wide
          >
            <img src={previewUrl} alt="Full preview" className={styles.fullPreviewImage} />
          </Modal>
        )}
      </div>
    );
  }

  // ── Empty state: compact horizontal strip ──
  return (
    <div className={styles.analyzer}>
      <div className={styles.emptyState}>
        <div className={styles.emptyLeft}>
          <div className={styles.title}>{hasAiAccess ? t('aiScan.title') : t('aiScan.uploadTitle')}</div>
          <div className={styles.subtitle}>{hasAiAccess ? t('aiScan.optional') : t('aiScan.uploadSubtitle')}</div>
        </div>

        <div
          className={`${styles.dropZone} ${dragging ? styles.dropZoneDragging : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className={styles.fileInput}
            onChange={handleInputChange}
          />
          {hasAiAccess ? (
            <>
              <div className={styles.aiIcon}>
                <AiSvgIcon />
              </div>
              <div className={styles.dropTextGroup}>
                <div className={styles.dropText}>{t('aiScan.dropText')}</div>
                <div className={styles.dropHint}>{t('aiScan.dropHint')}</div>
              </div>
            </>
          ) : (
            <div className={styles.dropTextGroup}>
              <div className={styles.dropText}>{t('aiScan.uploadDropText')}</div>
              <div className={styles.dropHint}>{t('aiScan.dropHint')}</div>
            </div>
          )}
        </div>
      </div>

      {error && <div className={styles.error} style={{ marginTop: '0.5rem' }}>{error}</div>}
    </div>
  );
}
